import CheckboxInput from 'components/Luxkit/Checkbox/CheckboxInput'
import Expander from 'components/utils/Expander'
import { MAX_COMPARISON_SELECTIONS } from 'constants/offer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addOfferComparisonSelection, removeOfferComparisonSelection, replaceOfferComparisonSelection } from 'actions/OfferComparisonActions'
import { selectOfferComparisonEnabled } from 'selectors/offerComparisonSelectors'
import uuidV4 from 'lib/string/uuidV4Utils'
import { stopPropagation } from 'lib/web/eventHandlerUtils'
import { rem } from 'polished'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import useOfferList from 'hooks/Offers/useOfferList'
import equal from 'fast-deep-equal'
import Tooltip from 'components/Luxkit/Tooltip'

const Root = styled.div`
  display: inline-block;
  padding-bottom: ${rem(12)};
`

interface Props {
  offerType: App.OfferType
  offerId: string
  name: string
  filters?: App.OfferListFilters
}

function SearchTileComparisonCheckbox({ offerType, offerId, name, filters }: Props) {
  const dispatch = useAppDispatch()
  const currentSelections = useAppSelector(state => state.offerComparison.selections)
  const comparisonEnabled = useAppSelector(selectOfferComparisonEnabled)
  const canAddSelection = currentSelections.length < MAX_COMPARISON_SELECTIONS
  const searchId = useOfferList(filters).searchId

  const [selectionId] = useState(() => uuidV4())

  const currentSelection = currentSelections.find(s => s.offerId === offerId)
  const isSelected = !!currentSelection

  const onToggle = useCallback(() => {
    if (currentSelection) {
      dispatch(removeOfferComparisonSelection(currentSelection.selectionId))
    } else {
      dispatch(addOfferComparisonSelection({ selectionId, offerType, offerId, name, params: { filters, searchId } }))
    }
  }, [dispatch, offerType, selectionId, offerId, name, filters, searchId, currentSelection])

  useEffect(() => {
    if (currentSelection) {
      // Replace selection if the details have changed
      const newSelection: App.OfferComparisonSelection = { selectionId, offerType, offerId, name, params: { filters, searchId } }
      if (!equal(currentSelection, newSelection)) {
        dispatch(replaceOfferComparisonSelection(newSelection))
      }
    }
  }, [currentSelection, dispatch, filters, name, offerId, offerType, searchId, selectionId])

  const checkboxDisabled = !isSelected && !canAddSelection

  return (
    <Expander expanded={comparisonEnabled}>
      <Tooltip description={`Only ${MAX_COMPARISON_SELECTIONS} offers can be compared at a time`} disabled={!comparisonEnabled || !checkboxDisabled}>
        <Root onClick={stopPropagation} aria-hidden={!comparisonEnabled}>
          <CheckboxInput
            onChange={onToggle}
            disabled={checkboxDisabled || !comparisonEnabled}
            checked={isSelected}
          >
            Compare
          </CheckboxInput>
        </Root>
      </Tooltip>
    </Expander>
  )
}

export default SearchTileComparisonCheckbox
